import { AlertMsgMngrService } from '@core_n/msg/alert-msg-mngr.service';
import { VideoMsgMngrService } from '@core_n/msg/video-mngr.service';
import { EventEmitter } from 'events';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfig } from '../app.config';
import { constsMngr } from './consts';
import * as enums from './enums';

export var NeWMI_VER = "2.4.13";

// User Data
export var UserLoggedIn = 'IsUserLoggedIn';
export var UserName = 'IsUserLoggedInUserName';
export var UserPerm = 'IsUserLoggedInPerm';
export var UserVideoToken = 'IsUserLoggedInVideoToken';
export var UserAlertToken = 'IsUserLoggedInAlertToken';


export var UserCanSeeVid = 'CanUserSeeVid';
export var UserCanSeeSolo = 'CanUserSeeSolo';
export var UserCanSeeAi = 'CanUserSeeAi';
export var UserCanSetChannel = 'CanSetChannel';
export var UserCanSetDaynamicChannel = 'CanSetDaynamicChannel';
export var UserCanSetAnalytics = 'CanSeeAnalytics';
export var UserCameraAdmin = 'CameraAdmin';

export var MAX_SPEED_KNOTS = 1630; // knots
export var MAX_ALT_FT = 82000.0; // ft
export var MISSILE_MAX_SPEED_KNOTS = 5500.0; // 5500 knots
export var MISSILE_MIN_SPEED_KNOTS = 1000.0; // 1000 knots
export var MISSILE_MAX_FT = 300000; // 25,000 ft

// TBM Min\Max Velocity
export function setMaxTbmVelocity(val: number) { // put knots pls
  MISSILE_MAX_SPEED_KNOTS = val;
}
export function setMinTbmVelocity(val: number) { // put knots pls
  MISSILE_MIN_SPEED_KNOTS = val;
}

// NAI NAT MAX/MIN ALT
export var MAXNAINATALTITUDE = 30; // KM
export var MINNAINATALTITUDE = -1; // KM
export function setMaxNaiNatAlt(val: number) {
  MAXNAINATALTITUDE = val;
}
export function setMinNaiNatAlt(val: number) {
  MINNAINATALTITUDE = val;
}

// credibility min\max
export var MAXRADARCREDIBILITY = 3;
export var MINRADARCREDIBILITY = 1;
export function setMaxCredibility(val: number) {
  MAXRADARCREDIBILITY = val;
}
export function setMinCredibility(val: number) {
  MINRADARCREDIBILITY = val;
}

export enum DisplayUnits {
  Imperial,
  Metric
};

export const DisplayUnitsTitles: string[] = [
  'Imperial',
  'Metric'
];

export interface NotificationSettings {
  alerts: boolean,
  videoAzimuth: boolean,
  sensorAzimuth: boolean
}

export function nextDisplayUnit(unit) {
  switch (unit) {
    case this.DisplayUnits.Metric:
      return this.DisplayUnits.Imperial;
    case this.DisplayUnits.Imperial:
      return this.DisplayUnits.Metric;
    default:
      return this.DisplayUnits.Metric;
  }
}

export var upperBarHeight = 0;
export function setUpperBarHeight(height) {
  upperBarHeight = height;
}

export var statusBarHeight = 0;
export function setStatusBarHeight(height) {
  statusBarHeight = height;
}

export enum DisplayFilter {
  Maps,
  NaiNat,
  AcousticVector,
  Sensors,
  Targets,
  Tracks,
  Plots
}

export const DisplayFilterTitles: string[] = [
  'Maps',
  'Nai & Nat',
  'Vector',
  'Points',
  'Targets',
  'Tracks',
  'Plots'
];

export class MapExtent {
  //def values are defaults on location on map at launch

  // defheight:10
  // defwidth:22.5
  // defxCenter:32
  // defyCenter:49
  // height:10.851562499999957
  // width:22.499999999999915
  // xCenter:32.63671875000004
  // yCenter:48.57890625000012


  xCenter: number;
  defxCenter: number = 32;
  yCenter: number;
  defyCenter: number = 49;
  width: number;
  defwidth: number = 22.5;
  height: number;
  defheight: number = 10;

  constructor(xCenter?: number, yCenter?: number, width?: number, height?: number) {
    this.xCenter = xCenter;
    this.yCenter = yCenter;
    this.width = width;
    this.height = height;

    this.defxCenter = Math.round(this.xCenter);
    this.defyCenter = Math.round(this.yCenter);
    this.defwidth = Math.round(this.width);
    this.defheight = Math.round(this.height);
  }

  clone(): MapExtent {
    let extent = new MapExtent(this.xCenter, this.yCenter, this.width, this.height);
    return extent;
  }

  getFromStorage(name: string) {
    let val = null;
    val = localStorage.getItem(`${name}XCenter`);
    this.xCenter = val == null ? this.defxCenter : Number(val);
    val = localStorage.getItem(`${name}YCenter`);
    this.yCenter = val == null ? this.defyCenter : Number(val);
    val = localStorage.getItem(`${name}Width`);
    this.width = val == null ? this.defwidth : Number(val);
    val = localStorage.getItem(`${name}Height`);
    this.height = val == null ? this.defheight : Number(val);

    this.defxCenter = Math.round(this.xCenter);
    this.defyCenter = Math.round(this.yCenter);
    this.defwidth = Math.round(this.width);
    this.defheight = Math.round(this.height);
  }

  setToStorage(name: string) {
    localStorage.setItem(`${name}XCenter`, this.xCenter.toString());
    localStorage.setItem(`${name}YCenter`, this.yCenter.toString());
    localStorage.setItem(`${name}Width`, this.width.toString());
    localStorage.setItem(`${name}Height`, this.height.toString());
  }
}

export var eventsEmitter = new EventEmitter();

export class UserSettings {
  euroVer: string = "";

  userName: string;
  userPerm: enums.Permission = enums.Permission.USER;
  userVideoToken: string;
  userAlertToken: string;
  public canSeeVid: boolean = false;
  public canSeeAi: boolean = false;
  public canSeeSolo: boolean = false;
  public canSetChannel: boolean = false;
  public canSetDaynamicChannel: boolean = false;
  public canSetAnalytics: boolean = false;
  public isCameraAdmin: boolean = false;

  displayUnits: DisplayUnits; // for all A/N displays
  public displayUnitsSub = new BehaviorSubject<DisplayUnits>(1);

  // sensor pool change indic
  public poolChanged = new BehaviorSubject<boolean>(false);

  //Playback mode
  public isPlayback = new BehaviorSubject<boolean>(false);
  public playbackTime = new BehaviorSubject<Date>(new Date);

  // close menus
  public menuCloser = new BehaviorSubject<boolean>(false);

  public halfSecInter = new BehaviorSubject<boolean>(false);
  public secInter = new BehaviorSubject<boolean>(false);

  // MapIndex / MapLayer
  private MapIndex = new BehaviorSubject<number>(undefined);

  public TriggerAlertWindow = new BehaviorSubject<boolean>(false);

  getMapIndexObs(): Observable<number> {
    return this.MapIndex.asObservable();
  }

  SetMapIndex(index: number) {
    var realIndex = this.MapIndex.value;
    if (index == undefined) {
      realIndex++;
      if (realIndex >= this.MapIndex.value)
        realIndex = 0;
    }
    else
      realIndex = index;
    this.MapIndex.next(realIndex);
  }

  coordinatesType: enums.CoordinateType;

  bgMapOpacity: number; // background map opacity
  IpFillOpacity: number; // Ip Fill Opacity
  TpFillOpacity: number; // Ip Fill Opacity
  symbolsSize: number; // map symbols scaling, default is 1.0
  autoSymbolSize: boolean; // auto adjust symbol size by zoom...

  toolBarX: number;
  toolBarY: number;

  filterOn: Array<boolean>;

  // map
  mapElementsOn: Array<boolean>;
  mapExtent: MapExtent = new MapExtent(31, 35, 23.3, 11.25);
  mapHome: MapExtent = new MapExtent(31, 35, 23.3, 11.25);

  // track
  trackOpacity: number;
  trackHostilityOn: Array<boolean>;
  trackElementsOn: Array<boolean>;
  sysTrackElementsOn: Array<boolean>;
  ipTrackElementsOn: Array<boolean>;
  IpTrackColor: string;
  IpTrackColorSmt: string;
  TpTrackColor: string;
  TpTrackColorSmt: string;
  snsTrackElementsOn: Array<boolean>;
  angTrackElementsOn: Array<boolean>;
  clsTrackElementsOn: Array<boolean>;
  identityTrackElementsOn: Array<boolean>;
  trackTrailLength: number;
  trackSourcesHidden: Set<number>; // hidden sensor IDs
  trackTextSize: number;
  trackTextColor: string;
  trackTextToggler: boolean;
  trackLifeTime: number;

  // plot
  plotOpacity: number;
  plotTrailLength: number = 60;
  plotDisplaySymbol: boolean; // True=Symbol, False=Letter
  plotSourcesHidden: Set<number>; // hidden sensor IDs
  plotTypeHidden: Array<boolean>; // Hidden Plot Types

  // point AKA Sensor
  pointOpacity: number;
  pointElementsOn: Array<boolean>;
  snsTypeElementsOn: Array<boolean>;
  acousticElementsOn: Array<boolean>;
  acousticSourcesOn: Array<boolean>;
  acousticVectorTypesOn: Record<string,boolean>;
  acousticTextColor: string;
  acousticSelectedColor: string;
  acousticOpacity: number; // Ip Fill Opacity
  acousticLifeTime: number;
  acousticUseWhiteListOn: boolean;

  // free drawing
  freeDrawColor: string;

  // ExIp
  ExIpToEdit: any = null; // externalIpObject
  ExIpColor: string;
  ExIpText: boolean = true;
  ExIpOpacity: number; // ExIp Fill Opacity

  // NaiNat
  nainatOpacity: number;
  naiActive: boolean;
  natActive: boolean;
  naiColor: string;
  natColor: string;
  naiFillOpacity: number;
  natFillOpacity: number;

  notifications: NotificationSettings = {
    alerts: false,
    videoAzimuth: true,
    sensorAzimuth: false
  }

  constructor(private videoMngrSrvc: VideoMsgMngrService, private alertMngr: AlertMsgMngrService) {
    let val = null;

    let numFilters = Object.keys(DisplayFilter).length / 2;
    this.filterOn = new Array<boolean>(numFilters);
    for (let i = 0; i < this.filterOn.length; i++) {
      val = localStorage.getItem(`Display${DisplayFilter[i]}`);
      this.filterOn[i] = val == null ? true : (val == 'true');
    }

    let numSysTrackElements = constsMngr.sysTrackDisplayElementCount();
    this.sysTrackElementsOn = new Array<boolean>(numSysTrackElements);
    for (let i = 0; i < this.sysTrackElementsOn.length; i++) {
      val = localStorage.getItem(`SysTrack${constsMngr.sysTrackDisplayElementTitle(i)}`);
      this.sysTrackElementsOn[i] = val == null ? true : (val == 'true');
    }

    this.getUserCredsFromSession();

    val = localStorage.getItem(`DisplayUnits`);
    this.displayUnits = val == null ? DisplayUnits.Metric : Number(val);
    this.displayUnitsSub.next(this.displayUnits);

    val = localStorage.getItem(`CoordinatesType`);
    this.coordinatesType = val == null ? enums.CoordinateType.WGS84_DMM : Number(val);

    val = localStorage.getItem('BgMapOpacity');
    this.bgMapOpacity = val == null ? 1.0 : Number(val);

    val = localStorage.getItem('IpFillOpacity');
    this.IpFillOpacity = val == null ? 0.2 : Number(val);

    val = localStorage.getItem('TpFillOpacity');
    this.TpFillOpacity = val == null ? 0.2 : Number(val);

    val = localStorage.getItem('SymbolsSize');
    this.symbolsSize = val == null ? 1.0 : Number(val);

    val = localStorage.getItem('autoSymbolSize');
    if (val)
      this.autoSymbolSize = val == 't' ? true : false;
    else
      this.autoSymbolSize = true;

    val = localStorage.getItem('ToolBarX');
    this.toolBarX = val == null ? 10 : Number(val);
    val = localStorage.getItem('ToolBarY');
    this.toolBarY = val == null ? 100 : Number(val);

    let numMapElements = constsMngr.mapDisplayElementCount();
    this.mapElementsOn = new Array<boolean>(numMapElements);
    for (let i = 0; i < this.mapElementsOn.length; i++) {
      val = localStorage.getItem(`Map${constsMngr.mapDisplayElementTitle(i)}`);
      this.mapElementsOn[i] = val == null ? true : (val == 'true');
    }

    this.mapExtent.getFromStorage('MapExtent');
    this.mapHome.getFromStorage('MapHome');

    // Track

    val = localStorage.getItem('TrackOpacity');
    this.trackOpacity = val == null ? 1.0 : Number(val);

    let numTrackIdentities = Object.keys(enums.Hostility).length / 2;
    this.trackHostilityOn = new Array<boolean>(numTrackIdentities);
    for (let i = 0; i < this.trackHostilityOn.length; i++) {
      val = localStorage.getItem(`TrackIdent${enums.Hostility[i]}`);
      this.trackHostilityOn[i] = val == null ? true : (val == 'true');
    }
    let numTrackElements = constsMngr.trackDisplayElementCount();
    this.trackElementsOn = new Array<boolean>(numTrackElements);
    for (let i = 0; i < this.trackElementsOn.length; i++) {
      val = localStorage.getItem(`Track${constsMngr.trackDisplayElementTitle(i)}`);
      this.trackElementsOn[i] = val == null ? true : (val == 'true');
    }

    let numSnsTrackElements = constsMngr.snsTrackDisplayElementCount();
    this.snsTrackElementsOn = new Array<boolean>(numSnsTrackElements);
    for (let i = 0; i < this.snsTrackElementsOn.length; i++) {
      val = localStorage.getItem(`SnsTrack${constsMngr.snsTrackDisplayElementTitle(i)}`);
      this.snsTrackElementsOn[i] = val == null ? true : (val == 'true');
    }
    let numAngTrackElements = constsMngr.angTrackDisplayElementCount();
    this.angTrackElementsOn = new Array<boolean>(numAngTrackElements);
    for (let i = 0; i < this.angTrackElementsOn.length; i++) {
      val = localStorage.getItem(`AngTrack${constsMngr.angTrackDisplayElementTitle(i)}`);
      this.angTrackElementsOn[i] = val == null ? true : (val == 'true');
    }
    let numClsTrackElements = constsMngr.clsTrackDisplayElementCount();
    this.clsTrackElementsOn = new Array<boolean>(numClsTrackElements);
    for (let i = 0; i < this.clsTrackElementsOn.length; i++) {
      val = localStorage.getItem(`ClsTrack${constsMngr.clsTrackDisplayElementTitle(i)}`);
      this.clsTrackElementsOn[i] = val == null ? true : (val == 'true');
    }

    let numIdentityTrackElements = constsMngr.identityTrackDisplayElementCount();
    this.identityTrackElementsOn = new Array<boolean>(numIdentityTrackElements);
    for (let i = 0; i < this.identityTrackElementsOn.length; i++) {
      val = localStorage.getItem(`IdentityTrack${constsMngr.identityTrackDisplayElementTitle(i)}`);
      this.identityTrackElementsOn[i] = val == null ? true : (val == 'true');
    }

    val = localStorage.getItem('TrackTrailLength');
    this.trackTrailLength = val == null ? 10 : Number(val);
    this.trackSourcesHidden = new Set();

    this.IpTrackColor = localStorage.getItem('IpTrackColor');
    if (!this.IpTrackColor) {
      this.IpTrackColor = "#00ff00";
    }

    this.IpTrackColorSmt = localStorage.getItem('IpTrackColorSmt');
    if (!this.IpTrackColorSmt) {
      this.IpTrackColorSmt = "#ff0000";
    }

    this.TpTrackColor = localStorage.getItem('TpTrackColor');
    if (!this.TpTrackColor) {
      this.TpTrackColor = "#00ff00";
    }

    this.TpTrackColorSmt = localStorage.getItem('TpTrackColorSmt');
    if (!this.TpTrackColorSmt) {
      this.TpTrackColorSmt = "#ff0000";
    }

    val = localStorage.getItem('trackTextSize');
    this.trackTextSize = val == null ? 1 : Number(val);

    this.trackTextColor = localStorage.getItem('trackTextColor');
    if (!this.trackTextColor) {
      this.trackTextColor = "#ff0000";
    }

    val = localStorage.getItem('trackTextToggler');
    if (val)
      this.trackTextToggler = val == 't' ? true : false;
    else
      this.trackTextToggler = true;

    val = localStorage.getItem('trackLifeTime');
    this.trackLifeTime = val == null ? 20 : Number(val);
    if (this.trackLifeTime < 5)
      this.trackLifeTime = 10;
    // EXIP

    this.ExIpColor = localStorage.getItem('ExIpFillColor');
    if (!this.ExIpColor) {
      this.ExIpColor = "#0000ff";
    }

    val = localStorage.getItem('ExIpFillOpacity');
    this.ExIpOpacity = val == null ? 1 : Number(val);

    val = localStorage.getItem('ExIpText');
    if (val)
      this.ExIpText = val == 't' ? true : false;
    else
      this.ExIpText = true;

    // Plot

    val = localStorage.getItem('PlotOpacity');
    this.plotOpacity = val == null ? 1.0 : Number(val);

    val = localStorage.getItem('PlotTrailLength');
    this.plotTrailLength = val == null ? 60 : Number(val);
    val = localStorage.getItem('PlotDisplaySymbol');
    this.plotDisplaySymbol = val == null ? true : (val == 'true');
    this.plotSourcesHidden = new Set();

    this.plotTypeHidden = new Array<boolean>(enums.PlotCorrTitles.length);
    for (let i = 0; i < this.plotTypeHidden.length; i++) {
      val = localStorage.getItem(`plotType${enums.PlotTypeTitle(i)}`);
      this.plotTypeHidden[i] = val == null ? true : (val == 'true');
    }

    // Point

    val = localStorage.getItem('PointOpacity');
    this.pointOpacity = val == null ? 1.0 : Number(val);

    let numPointElements = constsMngr.pointDisplayElementCount();
    this.pointElementsOn = new Array<boolean>(numPointElements);
    for (let i = 0; i < this.pointElementsOn.length; i++) {
      val = localStorage.getItem(`Point${constsMngr.pointDisplayElementTitle(i)}`);
      this.pointElementsOn[i] = val == null ? false : (val == 'true');
    }

    let numSnsTypeElements = constsMngr.snsTypeDisplayElementCount();
    this.snsTypeElementsOn = new Array<boolean>(numSnsTypeElements);
    for (let i = 0; i < this.snsTypeElementsOn.length; i++) {
      val = localStorage.getItem(`SnsType${constsMngr.snsTypeDisplayElementTitle(i)}`);
      let Option  = constsMngr.snsTypeDisplayElementOption(i)
      if(!Option)
        val = Option
      this.snsTypeElementsOn[i] = val == null ? true : (val == 'true');
    }

    let numAcousticElements = constsMngr.acousticDisplayElementCount();
    this.acousticElementsOn = new Array<boolean>(numAcousticElements);
    for (let i = 0; i < this.acousticElementsOn.length; i++) {
      val = localStorage.getItem(`Acoustic${constsMngr.acousticDisplayElementTitle(i)}`);
      this.acousticElementsOn[i] = val == null ? true : (val == 'true');
    }

    let numAcousticSourcesOn = enums.SourceTitles.length - 1;
    this.acousticSourcesOn = new Array<boolean>(numAcousticSourcesOn);
    for (let i = 0; i < this.acousticSourcesOn.length; i++) {
      val = localStorage.getItem(`AcousticSource${enums.SourceTitles[i]}`);
      this.acousticSourcesOn[i] = val == null ? true : (val == 'true');
    }

    this.initAcousticVectorType();

    this.acousticSelectedColor = localStorage.getItem('acousticSelectedColor');
    if (!this.acousticSelectedColor) {
      this.acousticSelectedColor = "#ffffff";
    }

    this.acousticTextColor = localStorage.getItem('acousticTextColor');
    if (!this.acousticTextColor) {
      this.acousticTextColor = "#ffffff";
    }

    val = localStorage.getItem('acousticOpacity');
    this.acousticOpacity = val == null ? 1.0 : Number(val);

    val = localStorage.getItem('acousticLifeTime');
    this.acousticLifeTime = val == null ? 100 : Number(val);

    val = localStorage.getItem("acousticUseWhiteListOn");
    this.acousticUseWhiteListOn = val == null ? true : (val == 'true');

    // Free drawing 

    val = localStorage.getItem('FreeDrawColor');
    this.freeDrawColor = val == null ? undefined : val;

    // nai nat
    val = localStorage.getItem('nainatOpacity');
    this.nainatOpacity = val == null ? 1.0 : Number(val);

    val = localStorage.getItem('naiActive');
    if (val)
      this.naiActive = val == 't' ? true : false;
    else
      this.naiActive = true;

    val = localStorage.getItem('natActive');
    if (val)
      this.natActive = val == 't' ? true : false;
    else
      this.natActive = true;

    this.naiColor = localStorage.getItem('naiColor');
    if (!this.naiColor) {
      this.naiColor = "#FFD800";
    }

    this.natColor = localStorage.getItem('natColor');
    if (!this.natColor) {
      this.natColor = "#FF006E";
    }

    val = localStorage.getItem('naiFillOpacity');
    this.naiFillOpacity = val == null ? 0.2 : Number(val);

    val = localStorage.getItem('natFillOpacity');
    this.natFillOpacity = val == null ? 0.2 : Number(val);

    let self = this;
    setInterval(function () { self.invert() }, 500);

    this.notifications = this.getNotifications()
  }

  initAcousticVectorType() {
    const val = localStorage.getItem(`AcousticVectorTypes`);
    if(val)
    {
      this.acousticVectorTypesOn = JSON.parse(val);
    }
    else {
      this.acousticVectorTypesOn = Object.keys(AppConfig.settings.colorScale || {}).reduce((acc,key) => {
        acc[key] = true; 
        return acc;
      }, {}) as Record<string, boolean>;
    }
  }

  c: number = 0;
  private invert() {
    this.halfSecInter.next(!this.halfSecInter.getValue());
    this.c++;
    if (this.c >= 2) {
      this.c = 0;
      this.secInter.next(!this.secInter.getValue());
    }
  }

  public getUserCredsFromSession() {
    if (sessionStorage.getItem(UserLoggedIn) == 'true' ? true : false) {
      this.userName = sessionStorage.getItem(UserName);
      this.userPerm = Number.parseInt(sessionStorage.getItem(UserPerm));
      this.userVideoToken = sessionStorage.getItem(UserVideoToken);
      this.userAlertToken = sessionStorage.getItem(UserAlertToken);
      this.canSeeVid = sessionStorage.getItem(UserCanSeeVid) == 'true' ? true : false;
      this.canSeeAi = sessionStorage.getItem(UserCanSeeAi) == 'true' ? true : false;
      this.canSeeSolo = sessionStorage.getItem(UserCanSeeSolo) == 'true' ? true : false;
      this.canSetChannel = sessionStorage.getItem(UserCanSetChannel) == 'true' ? true : false;
      this.canSetDaynamicChannel = sessionStorage.getItem(UserCanSetDaynamicChannel) == 'true' ? true : false;
      this.canSetAnalytics = sessionStorage.getItem(UserCanSetAnalytics) == 'true' ? true : false;
      this.isCameraAdmin = sessionStorage.getItem(UserCameraAdmin) == 'true' ? true : false;
      if (this.userVideoToken && this.canSeeVid) {
        this.videoMngrSrvc.setToken(this.userVideoToken);
        this.videoMngrSrvc.getSensorsList();
        if (this.userAlertToken)
          this.alertMngr.setToken(this.userAlertToken);
      }
      this.setSettingsByPerm();
    }
  }

  public setSettingsByPerm() {
    switch (this.userPerm) {
      case enums.Permission.ADMIN:
        this.userPerm = enums.Permission.ADMIN;
        let val = null;
        for (let i = 0; i < this.filterOn.length; i++) {
          val = localStorage.getItem(`Display${DisplayFilter[i]}`);
          this.filterOn[i] = val == null ? true : (val == 'true');
        }
        for (let i = 0; i < this.sysTrackElementsOn.length; i++) {
          val = localStorage.getItem(`SysTrack${constsMngr.sysTrackDisplayElementTitle(i)}`);
          this.sysTrackElementsOn[i] = val == null ? true : (val == 'true');
        }
        break;

      case enums.Permission.USER:
        this.userPerm = enums.Permission.USER;

        this.filterOn[DisplayFilter.NaiNat] = false;
        this.filterOn[DisplayFilter.Plots] = false;
        this.filterOn[DisplayFilter.Sensors] = false;
        this.filterOn[DisplayFilter.Targets] = false;

        for (let i = 0; i < 4; i++)
          this.sysTrackElementsOn[i] = false;


        break;
      default:
        this.userPerm = enums.Permission.USER;
        break;
    }
  }

  setUserName(userName: string) {
    this.userName = userName;
    localStorage.setItem(`UserName`, userName);
  }

  setDisplayUnits(displayUnits: DisplayUnits) {
    this.displayUnits = displayUnits;
    this.displayUnitsSub.next(displayUnits);
    localStorage.setItem(`DisplayUnits`, displayUnits.toString());
  }

  setCoordinatesType(coordinatesType: enums.CoordinateType) {
    this.coordinatesType = coordinatesType;
    localStorage.setItem(`CoordinatesType`, coordinatesType.toString());
  }

  setBgMapOpacity(opacity: number) {
    this.bgMapOpacity = opacity;
    localStorage.setItem(`BgMapOpacity`, opacity.toString());
  }

  setIpFillOpacity(opacity: number) {
    this.IpFillOpacity = opacity;
    localStorage.setItem(`IpFillOpacity`, opacity.toString());
  }

  setTpFillOpacity(opacity: number) {
    this.TpFillOpacity = opacity;
    localStorage.setItem(`TpFillOpacity`, opacity.toString());
  }

  setSymbolsSize(size: number) {
    this.symbolsSize = size;
    localStorage.setItem(`SymbolsSize`, size.toString());
  }

  setAutoSymbolSize(size: boolean) {
    this.autoSymbolSize = size;
    localStorage.setItem(`autoSymbolSize`, this.autoSymbolSize ? 't' : 'f');
  }

  setToolBarPosition(x: number, y: number) {
    this.toolBarX = x;
    this.toolBarY = y;
    localStorage.setItem(`ToolBarX`, x.toString());
    localStorage.setItem(`ToolBarY`, y.toString());
  }

  toggleFilterOn(filter) {
    this.filterOn[filter] = !this.filterOn[filter];
    localStorage.setItem(`Display${DisplayFilter[filter]}`, this.filterOn[filter] ? 'true' : 'false');
  }

  setMapExtent(extent: MapExtent) {
    this.mapExtent = extent;
    this.mapExtent.setToStorage('MapExtent');
  }

  setMapHome(extent: MapExtent) {
    this.mapHome = extent;
    this.mapHome.setToStorage('MapHome');
  }

  // Track

  setTrackOpacity(opacity: number) {
    this.trackOpacity = opacity;
    localStorage.setItem(`TrackOpacity`, opacity.toString());
  }

  toggleMapElementOn(mapElem) {
    this.mapElementsOn[mapElem] = !this.mapElementsOn[mapElem];
    localStorage.setItem(`Map${constsMngr.mapDisplayElementTitle(mapElem)}`, this.mapElementsOn[mapElem] ? 'true' : 'false');
  }

  toggleTrackHostilityOn(trackIdent) {
    this.trackHostilityOn[trackIdent] = !this.trackHostilityOn[trackIdent];
    localStorage.setItem(`TrackIdent${enums.Hostility[trackIdent]}`, this.trackHostilityOn[trackIdent] ? 'true' : 'false');
    eventsEmitter.emit('TrackIdentityToggled', trackIdent);
  }

  toggleAllTrackHostility(onOff: boolean) {
    for (let i = 0; i < this.trackHostilityOn.length; i++) {
      this.identityTrackElementsOn[i] = onOff;
      localStorage.setItem(`IdentityTrack${constsMngr.identityTrackDisplayElementTitle(i)}`, this.identityTrackElementsOn[i] ? 'true' : 'false');
    }
    eventsEmitter.emit('TrackIdentityToggled', onOff ? -1 : -2);
  }

  toggleTrackElementOn(trackElem) {
    this.trackElementsOn[trackElem] = !this.trackElementsOn[trackElem];
    localStorage.setItem(`Track${constsMngr.trackDisplayElementTitle(trackElem)}`, this.trackElementsOn[trackElem] ? 'true' : 'false');
  }

  toggleSysTrackElementOn(trackElem) {
    this.sysTrackElementsOn[trackElem] = !this.sysTrackElementsOn[trackElem];
    localStorage.setItem(`SysTrack${constsMngr.sysTrackDisplayElementTitle(trackElem)}`, this.sysTrackElementsOn[trackElem] ? 'true' : 'false');
  }

  toggleSnsTrackElementOn(trackElem) {
    this.snsTrackElementsOn[trackElem] = !this.snsTrackElementsOn[trackElem];
    localStorage.setItem(`SnsTrack${constsMngr.snsTrackDisplayElementTitle(trackElem)}`, this.snsTrackElementsOn[trackElem] ? 'true' : 'false');
  }

  toggleAngTrackElementOn(trackElem) {
    this.angTrackElementsOn[trackElem] = !this.angTrackElementsOn[trackElem];
    localStorage.setItem(`AngTrack${constsMngr.angTrackDisplayElementTitle(trackElem)}`, this.angTrackElementsOn[trackElem] ? 'true' : 'false');
  }

  toggleClsTrackElementOn(trackElem) {
    this.clsTrackElementsOn[trackElem] = !this.clsTrackElementsOn[trackElem];
    localStorage.setItem(`ClsTrack${constsMngr.clsTrackDisplayElementTitle(trackElem)}`, this.clsTrackElementsOn[trackElem] ? 'true' : 'false');
  }

  toggleIdentityTrackElementOn(trackElem) {
    this.identityTrackElementsOn[trackElem] = !this.identityTrackElementsOn[trackElem];
    localStorage.setItem(`IdentityTrack${constsMngr.identityTrackDisplayElementTitle(trackElem)}`, this.identityTrackElementsOn[trackElem] ? 'true' : 'false');
    eventsEmitter.emit('TrackIdentityToggled', trackElem);
  }

  setTrackTrailLength(length: number) {
    this.trackTrailLength = length;
    localStorage.setItem('TrackTrailLength', this.trackTrailLength.toString());
  }

  hideTrackSource(sensorID: number) {
    this.trackSourcesHidden.add(sensorID);
    eventsEmitter.emit('TrackSourceHidden', sensorID);
  }

  showTrackSource(sensorID: number) {
    this.trackSourcesHidden.delete(sensorID);
    eventsEmitter.emit('TrackSourceShown', sensorID);
  }

  // Track Ip
  setIpColor(color: string) {
    this.IpTrackColor = color;
    localStorage.setItem(`IpTrackColor`, this.IpTrackColor);
  }

  // Track Ip smt
  setIpColorSmt(color: string) {
    this.IpTrackColorSmt = color;
    localStorage.setItem(`IpTrackColorSmt`, this.IpTrackColorSmt);
  }

  // Track Tp
  setTpColor(color: string) {
    this.TpTrackColor = color;
    localStorage.setItem(`TpTrackColor`, this.TpTrackColor);
  }

  // Track Tp Smt
  setTpColorSmt(color: string) {
    this.TpTrackColorSmt = color;
    localStorage.setItem(`TpTrackColorSmt`, this.TpTrackColorSmt);
  }

  // Track Text Size
  setTrackTextSize(Size: number) {
    this.trackTextSize = Size;
    localStorage.setItem(`trackTextSize`, this.trackTextSize.toString());
  }

  // Track Text Color
  setTrackTextColor(color: string) {
    this.trackTextColor = color;
    localStorage.setItem(`trackTextColor`, this.trackTextColor);
  }

  // Track Text Toggler
  setTrackTextToggler(bool: boolean) {
    this.trackTextToggler = bool;
    localStorage.setItem(`trackTextToggler`, this.trackTextToggler ? 't' : 'f');
  }

  setTrackLifeTime(length: number) {
    this.trackLifeTime = length;
    localStorage.setItem('trackLifeTime', this.trackLifeTime.toString());
  }

  // Plot

  setPlotOpacity(opacity: number) {
    this.plotOpacity = opacity;
    localStorage.setItem(`PlotOpacity`, opacity.toString());
  }

  setPlotTrailLength(length: number) {
    this.plotTrailLength = length;
    localStorage.setItem('PlotTrailLength', this.plotTrailLength.toString());
  }

  setPlotDisplaySymbol(symbol: boolean) {
    this.plotDisplaySymbol = symbol;
    localStorage.setItem('PlotDisplaySymbol', this.plotDisplaySymbol ? 'true' : 'false');
  }

  hidePlotSource(sensorID: number) {
    this.plotSourcesHidden.add(sensorID);
    eventsEmitter.emit('PlotSourceHidden', sensorID);
  }

  hidePlotType(type: number) {
    eventsEmitter.emit('PlotTypeHidden', type);
  }

  showPlotSource(sensorID: number) {
    this.plotSourcesHidden.delete(sensorID);
    eventsEmitter.emit('PlotSourceShown', sensorID);
  }

  showPlotSourcenodel(sensorID: number) {
    eventsEmitter.emit('PlotSourceShown', sensorID);
  }

  showPlotType(type: number) {
    eventsEmitter.emit('PlotTypeShown', type);
  }

  // Point

  setSensorsOpacity(opacity: number) {
    this.pointOpacity = opacity;
    localStorage.setItem(`PointOpacity`, opacity.toString());
  }

  togglePointElementOn(pointElem) {
    this.pointElementsOn[pointElem] = !this.pointElementsOn[pointElem];
    localStorage.setItem(`Point${constsMngr.pointDisplayElementTitle(pointElem)}`, this.pointElementsOn[pointElem] ? 'true' : 'false');
  }

  toggleSnsTypeElementOn(SnsTypeElem) {
    this.snsTypeElementsOn[SnsTypeElem] = !this.snsTypeElementsOn[SnsTypeElem];
    localStorage.setItem(`SnsType${constsMngr.snsTypeDisplayElementTitle(SnsTypeElem)}`, this.snsTypeElementsOn[SnsTypeElem] ? 'true' : 'false');
  }

  toggleAcousticSourceOn(Source) {
    this.acousticSourcesOn[Source] = !this.acousticSourcesOn[Source];
    localStorage.setItem(`AcousticSource${enums.SourceTitles[Source]}`, this.acousticSourcesOn[Source] ? 'true' : 'false');
  }

  toggleAcousticVectorTypeOn(key: string) {
    this.acousticVectorTypesOn[key] = !this.acousticVectorTypesOn[key];
    localStorage.setItem(`AcousticVectorTypes`, JSON.stringify(this.acousticVectorTypesOn));
  }

  toggleAcousticElementOn(AcousticElem) {
    this.acousticElementsOn[AcousticElem] = !this.acousticElementsOn[AcousticElem];
    localStorage.setItem(`Acoustic${constsMngr.snsTypeDisplayElementTitle(AcousticElem)}`, this.acousticElementsOn[AcousticElem] ? 'true' : 'false');
  }

  setAcousticSelectedColor(color: string) {
    this.acousticSelectedColor = color;
    localStorage.setItem(`acousticSelectedColor`, this.acousticSelectedColor);
  }

  setAcousticTextColor(color: string) {
    this.acousticTextColor = color;
    localStorage.setItem(`acousticTextColor`, this.acousticTextColor);
  }

  setAcousticOpacity(opacity: number) {
    this.acousticOpacity = opacity;
    localStorage.setItem(`acousticOpacity`, opacity.toString());
  }

  setAcousticLifeTime(LifeTime: number) {
    this.acousticLifeTime = LifeTime;
    localStorage.setItem(`acousticLifeTime`, LifeTime.toString());
  }

  setAcousticUseWhiteListOn(On: boolean) {
    this.acousticUseWhiteListOn = On;
    localStorage.setItem(`acousticUseWhiteListOn`, On ? 'true' : 'false');
  }

  // Free drawing

  setFreeDrawColor(color: string) {
    this.freeDrawColor = color;
    localStorage.setItem(`FreeDrawColor`, this.freeDrawColor);
  }

  // ExternalIp

  getEditExIp(): any {
    let ExIp = this.ExIpToEdit;
    this.ExIpToEdit = null;
    return ExIp;
  }

  setExIpFillColor(color: string) {
    this.ExIpColor = color;
    localStorage.setItem(`ExIpFillColor`, this.ExIpColor);
  }

  setExIpText(text: boolean) {
    this.ExIpText = text;
    localStorage.setItem(`ExIpText`, text ? "t" : "f");
  }

  setExIpFillOpacity(opacity: number) {
    this.ExIpOpacity = opacity;
    localStorage.setItem(`ExIpFillOpacity`, opacity.toString());
  }

  // nai nat

  setNaiNatOpacity(opacity: number) {
    this.nainatOpacity = opacity;
    localStorage.setItem(`nainatOpacity`, opacity.toString());
  }

  setNaiActive(isActive: boolean) {
    this.naiActive = isActive;
    localStorage.setItem(`naiActive`, isActive ? 't' : 'f');
  }

  setNatActive(isActive: boolean) {
    this.natActive = isActive;
    localStorage.setItem(`natActive`, isActive ? 't' : 'f');
  }

  setNaiColor(color: string) {
    this.naiColor = color;
    localStorage.setItem(`naiColor`, color);
  }

  setNatColor(color: string) {
    this.natColor = color;
    localStorage.setItem(`natColor`, color);
  }

  setNaiFillOpacity(opacity: number) {
    this.naiFillOpacity = opacity;
    localStorage.setItem(`naiFillOpacity`, opacity.toString());
  }

  setNatFillOpacity(opacity: number) {
    this.natFillOpacity = opacity;
    localStorage.setItem(`natFillOpacity`, opacity.toString());
  }

  setViewPerms() {
    if (!this.canSeeSolo)
      this.acousticSourcesOn[0] = false;
    else if (!this.acousticSourcesOn[0])
      this.acousticSourcesOn[0] = true;
    if (!this.canSeeAi)
      this.acousticSourcesOn[1] = false;
    else if (!this.acousticSourcesOn[1])
      this.acousticSourcesOn[1] = true;
    for (let i = 0; i < this.acousticSourcesOn.length; i++) {
      localStorage.setItem(`AcousticSource${enums.SourceTitles[i]}`, this.acousticSourcesOn[i] ? 'true' : 'false');
    }
  }
  
  getNotifications() {
    const notificationsString = localStorage.getItem(`notificationSettings`);
    if (notificationsString)
      return JSON.parse(notificationsString);

    return this.notifications
  }
  setNotifications(notifications: NotificationSettings) {
    this.notifications = notifications;
    localStorage.setItem(`notificationSettings`, JSON.stringify(notifications));
  }
}

export var userSettings: UserSettings;
export function initUserSettings(videoMngrSrvc, alertMngr) {
  if (userSettings == undefined)
    userSettings = new UserSettings(videoMngrSrvc, alertMngr);
}
